// src/features/payment/hooks/useOrderProcessing.ts
import { useEffect, useState } from "react";
import { useCartData } from "./useCartData";
import { useCustomerData } from "./useCustomerData";
import { useCurrentTime } from "./useCurrentTime";
import api from "../../../../../../features/auth/axiosConfig";
import { usePayment } from "./usePayment";
import { errorHandling } from "src/shared/utils/errorHandling";

export const useOrderProcessing = (cartId?: string | null) => {
  const [backToPayment, setBackToPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [showBalance, setShowBalance] = useState(false);
  const [prvDelete, setPrvDelete] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const { cartProducts, loading: cartLoading } = useCartData(cartId);
  const customerId = cartProducts?.[0]?.Customerid || "";
  const { customer } = useCustomerData(customerId);
  const { currentTime } = useCurrentTime();
  const { roundAmount } = usePayment();

  useEffect(() => {
    if (customer?.total_outstanding_balance && parseFloat(customer.total_outstanding_balance) <= 0) {
      setShowBalance(true);
    } else {
      setShowBalance(false);
    }
  }, [customer?.total_outstanding_balance]);

  useEffect(() => {
    setReceivedAmount(0);
  }, [showBalance]);

  const generateOrderId = () => {
    const now = new Date();
    return `${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(
      now.getMinutes(),
    ).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
  };

  const handleOrderSuccess = async (method: "Cash" | "Buy on Credit" | "Card") => {
    setButtonLoading(true);
    const orderAmount = cartProducts.reduce((sum, item) => sum + parseFloat(item.totalAmount), 0);
    const discount = cartProducts.reduce((sum, item) => sum + parseFloat(item.discount || "0"), 0);
    const deliveryFee = cartProducts.length > 0 ? Number(cartProducts[0].deliveryFee || 0) : 0;
    const finalAmount = orderAmount - discount + deliveryFee;
    const outstandingBalance = parseFloat(customer?.total_outstanding_balance || "0");
    const totalPayable = method === "Cash" ? (showBalance ? finalAmount + outstandingBalance : finalAmount) : finalAmount;

    // if (method === "Cash" && receivedAmount <= 0) {
    //   notifyWarning("Please enter received amount");
    //   return;
    // }

    const now = new Date();
    const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
    const newOrderId = generateOrderId();

    const order = {
      Customerid: cartProducts[0]?.Customerid,
      Products: cartProducts[0]?.products,
      Orderid: newOrderId,
      OrderStatus: "Confirm",
      Orderdate: localDate,
      Ordertime: now.toTimeString().split(" ")[0],
      totalAmount: orderAmount.toString(),
      discount: discount.toString(),
      deliveryFee: deliveryFee.toString(),
      Finalamount: finalAmount.toString(),
      outstanding_balance: customer?.total_outstanding_balance || "0",
      cancel_order_amount: "0",
      payments: [{ Paymentmethod: method, ReceivedAmount: method !== "Buy on Credit" ? receivedAmount.toString() : "0" }],
      paymentHistory: [{ Paymentmethod: method, ReceivedAmount: method !== "Buy on Credit" ? receivedAmount.toString() : "0" }],
      RemainingAmount: method !== "Buy on Credit" ? Math.max(finalAmount - receivedAmount, 0).toString() : finalAmount.toString(),
    };

    try {
      const response = await api.post(`/app/v1/pos/order`, order);
      // Use the order id the backend actually stored (authoritative), not the
      // client-generated timestamp, so the Done screen fetches the right order.
      const createdOrderId = response.data.Id || newOrderId;
      if (createdOrderId) {
        await api.delete(`/app/v1/pos/shopping_cart/${cartId}`);
        setPrvDelete(createdOrderId);
        setOrderId(createdOrderId);
        setPaymentSuccess(true);
      }
    } catch (error) {
      console.error("Error saving order:", error);
      errorHandling(error);
      setButtonLoading(false);
    }
  };

  return {
    cartProducts,
    customer,
    cartLoading,
    // customerLoading,
    backToPayment,
    setBackToPayment,
    paymentSuccess,
    orderId,
    receivedAmount,
    setReceivedAmount,
    showBalance,
    setShowBalance,
    roundAmount,
    currentTime,
    handleOrderSuccess,
    prvDelete,
    buttonLoading,
  };
};
